import { useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { ArrowLeft, Download } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useCostingMovementReport } from "~/hooks/costing";
import { exportCsv } from "~/utils/csv";

import {
  PeriodSelector,
  fmtCLP,
  fmtQty,
  useCosteoPeriod,
} from "./components/CosteoShared";
import {
  DailyMovementDialog,
  type DailyMovementItem,
} from "./components/DailyMovementDialog";

/** Movimiento de Producto Terminado — mirrors the reference accounting report:
 *  Inicial + Producción = Disponibilidad − Ventas + Devoluciones ⇒ Costo de Ventas
 *  e Inventario Final teórico (= Disponibilidad − Costo de Ventas). */
export function CosteoMovimientoPtPage() {
  const navigate = useNavigate();
  const { periods, period, setPeriod } = useCosteoPeriod();
  const { data: report = [], isLoading } = useCostingMovementReport(
    period,
    "finished_good"
  );

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<DailyMovementItem | null>(null);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return report.filter(
      (r) =>
        !q ||
        (r.item_code ?? "").toLowerCase().includes(q) ||
        (r.item_name ?? "").toLowerCase().includes(q)
    );
  }, [report, search]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (t, r) => ({
          opening: t.opening + Number(r.opening_value),
          production: t.production + Number(r.production_value),
          availability: t.availability + Number(r.availability_value),
          costOfSales: t.costOfSales + Number(r.cost_of_sales_value),
          adjustments:
            t.adjustments +
            Number(r.adjustments_in_value) -
            Number(r.adjustments_out_value) -
            Number(r.waste_value) -
            Number(r.revaluation_value),
          closing: t.closing + Number(r.closing_value),
        }),
        {
          opening: 0,
          production: 0,
          availability: 0,
          costOfSales: 0,
          adjustments: 0,
          closing: 0,
        }
      ),
    [rows]
  );

  const handleExport = () => {
    exportCsv(
      `movimiento_producto_terminado_${period}.csv`,
      [
        "Código",
        "Producto",
        "Inv. Inicial (Cant)",
        "Inv. Inicial ($)",
        "Producción (Cant)",
        "Producción ($)",
        "Disponibilidad (Cant)",
        "Disponibilidad ($)",
        "Costo Promedio",
        "Ventas (Cant)",
        "Devoluciones (Cant)",
        "Costo de Ventas (Cant)",
        "Costo de Ventas ($)",
        "Mermas/Ajustes (Cant)",
        "Mermas/Ajustes ($)",
        "Revalorización ($)",
        "Inv. Final Teórico (Cant)",
        "Inv. Final Teórico ($)",
      ],
      rows.map((r) => [
        r.item_code,
        r.item_name,
        r.opening_qty,
        r.opening_value,
        r.production_qty,
        r.production_value,
        r.availability_qty,
        r.availability_value,
        r.wac_clp,
        r.sales_qty,
        r.returns_qty,
        r.cost_of_sales_qty,
        r.cost_of_sales_value,
        r.adjustments_in_qty - r.adjustments_out_qty - r.waste_qty,
        r.adjustments_in_value - r.adjustments_out_value - r.waste_value,
        r.revaluation_value,
        r.closing_qty,
        r.closing_value,
      ])
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Costeo de Inventario"
        description="Inicial + producción = disponibilidad − ventas + devoluciones ⇒ costo de ventas e inventario final teórico · haz clic en un producto para el detalle diario"
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/costeo")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <>
            <PeriodSelector
              periods={periods}
              value={period}
              onChange={setPeriod}
            />
            <Button variant="outline" className="h-9" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Exportar CSV
            </Button>
          </>
        }
      />

      {/* Search bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar código o producto…"
          className="h-9 w-full sm:max-w-xs"
        />
      </div>

      {/* Main table */}
      {isLoading ? (
        <TableSkeleton rows={8} columns={9} />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">
                      Producto
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Inv. Inicial
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Producción
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Disponibilidad
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      WAC
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Ventas
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Devol.
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Costo de Ventas
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Mermas/Ajustes
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Inv. Final Teórico
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        className="py-8 text-center text-muted-foreground"
                      >
                        Sin movimientos de producto terminado en{" "}
                        {period || "este período"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r, index) => {
                      const adjQty =
                        Number(r.adjustments_in_qty) -
                        Number(r.adjustments_out_qty) -
                        Number(r.waste_qty);
                      const adjValue =
                        Number(r.adjustments_in_value) -
                        Number(r.adjustments_out_value) -
                        Number(r.waste_value) -
                        Number(r.revaluation_value);
                      return (
                        <TableRow
                          key={r.product_id}
                        onClick={() =>
                          setSelected({
                            kind: "finished_good",
                            id: r.product_id as string,
                            code: r.item_code,
                            name: r.item_name,
                          })
                        }
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                        className={`cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50 ${
                          Number(r.closing_qty) < 0 ? "bg-red-50" : ""
                        }`}
                      >
                        <TableCell>
                          <div className="font-medium">{r.item_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {r.item_code}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div>{fmtQty(r.opening_qty)}</div>
                          <div className="text-xs text-muted-foreground">
                            {fmtCLP(r.opening_value)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div>{fmtQty(r.production_qty)}</div>
                          <div className="text-xs text-muted-foreground">
                            {fmtCLP(r.production_value)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-medium">
                            {fmtQty(r.availability_qty)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {fmtCLP(r.availability_value)}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right font-medium">
                          {fmtCLP(r.wac_clp)}
                        </TableCell>
                        <TableCell className="text-right">
                          {fmtQty(r.sales_qty)}
                        </TableCell>
                        <TableCell className="text-right">
                          {fmtQty(r.returns_qty)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-medium">
                            {fmtCLP(r.cost_of_sales_value)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {fmtQty(r.cost_of_sales_qty)} u
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {adjQty === 0 ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <>
                              <div
                                className={`font-medium ${
                                  adjQty < 0
                                    ? "text-red-700"
                                    : "text-green-700"
                                }`}
                              >
                                {fmtQty(adjQty)}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {fmtCLP(adjValue)}
                              </div>
                            </>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div
                            className={`font-medium ${
                              Number(r.closing_qty) < 0
                                ? "text-destructive"
                                : ""
                            }`}
                          >
                            {fmtQty(r.closing_qty)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {fmtCLP(r.closing_value)}
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            {rows.length > 0 && (
              <div className="grid grid-cols-2 gap-4 border-t bg-muted/30 px-4 py-3 text-sm sm:grid-cols-6">
                <div>
                  <div className="text-muted-foreground">Inv. Inicial</div>
                  <div className="font-semibold">{fmtCLP(totals.opening)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Producción</div>
                  <div className="font-semibold">
                    {fmtCLP(totals.production)}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Disponibilidad</div>
                  <div className="font-semibold">
                    {fmtCLP(totals.availability)}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Costo de Ventas</div>
                  <div className="font-semibold">
                    {fmtCLP(totals.costOfSales)}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Mermas/Ajustes</div>
                  <div
                    className={`font-semibold ${
                      totals.adjustments < 0
                        ? "text-red-700"
                        : totals.adjustments > 0
                          ? "text-green-700"
                          : ""
                    }`}
                  >
                    {fmtCLP(totals.adjustments)}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">
                    Inv. Final Teórico
                  </div>
                  <div className="font-semibold">{fmtCLP(totals.closing)}</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <DailyMovementDialog
        period={period}
        item={selected}
        onClose={() => setSelected(null)}
      />
    </PageContainer>
  );
}
